/**
 * EmergencyBellClickLayer — 시뮬레이터 전용 비상벨 클릭 레이어 (opt-in).
 *
 * 맵 위 비상벨 마커를 클릭하면 simInteractionStore 에 트리거 요청을 게시한다.
 * 렌더링은 하지 않고 paper 의 cell:pointerclick 만 듣는 부수효과 컴포넌트다.
 * 이 컴포넌트는 bellClick prop 이 전달된 경우(=시뮬레이터 비상벨 탭)에만
 * FloorCanvas 가 마운트하므로, 에디터/뷰어 동작에는 영향이 없다.
 *
 * 매칭 전략(우선순위):
 *   1) 셀 id === bell.id (백엔드 export id 가 graphJson 셀 id 와 동일한 경우)
 *   2) 엘리먼트 이름(getCctvNameFromElement 규칙) === bell.name
 *   3) 엘리먼트 이름 === bell.linked_cctv_id (이름이 연결 CCTV 로 저장된 경우)
 * 어느 것도 맞지 않으면 클릭을 무시한다(다른 셀 클릭에 반응하지 않음).
 */
import { useEffect } from 'react';
import { dia } from '@joint/core';
import type { SimulatorEmergencyBell } from '../../../../models/Simulator';
import { useSimInteractionStore } from '../../../../pages/SimulatorPage/model/simInteractionStore';

export interface EmergencyBellClickLayerProps {
  /** JointJS graph instance */
  graph: dia.Graph;
  /** JointJS paper instance — 클릭 이벤트 소스 */
  paper: dia.Paper;
  /** 현재 맵의 비상벨 목록 */
  bells: SimulatorEmergencyBell[];
}

/** 이름 정규화 — 맵 라벨/속성과 비상벨 name 의 대소문자/공백 차이를 흡수한다. */
function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * 엘리먼트의 식별 이름 추출 — StatusOverlay.getCctvNameFromElement 와 동일 규칙.
 * data.properties.name → label/text → id 순으로 폴백한다.
 */
function getElementName(element: dia.Element): string {
  const data = element.get('data') as
    | { properties?: { name?: string } }
    | undefined;
  const propName = data?.properties?.name;
  if (propName) return propName;
  const labelText = element.attr('label/text') as string | undefined;
  if (labelText) return labelText;
  return String(element.id);
}

export function EmergencyBellClickLayer({
  graph,
  paper,
  bells,
}: EmergencyBellClickLayerProps) {
  useEffect(() => {
    if (bells.length === 0) return;

    // 빠른 조회용 인덱스 — id / 정규화 name / 정규화 linked_cctv_id.
    const byId = new Map<string, SimulatorEmergencyBell>();
    const byName = new Map<string, SimulatorEmergencyBell>();
    const byLinked = new Map<string, SimulatorEmergencyBell>();
    bells.forEach(b => {
      byId.set(String(b.id), b);
      if (b.name) byName.set(normalizeName(b.name), b);
      if (b.linked_cctv_id) byLinked.set(normalizeName(b.linked_cctv_id), b);
    });

    const matchBell = (element: dia.Element): SimulatorEmergencyBell | undefined => {
      const idHit = byId.get(String(element.id));
      if (idHit) return idHit;
      const name = normalizeName(getElementName(element));
      return byName.get(name) ?? byLinked.get(name);
    };

    // cursor:pointer 어포던스 — 매칭되는 셀에 한해(가능하면) 손가락 커서.
    // JointJS view 의 root 노드에 직접 style 을 입힌다. 실패해도(테스트 mock 등)
    // 클릭 동작 자체는 유지되도록 try/catch.
    const applyCursorAffordance = () => {
      try {
        graph.getElements().forEach(element => {
          if (!matchBell(element)) return;
          const view = paper.findViewByModel(element);
          const node = view?.el as HTMLElement | SVGElement | undefined;
          if (node && 'style' in node) {
            (node as unknown as { style: CSSStyleDeclaration }).style.cursor = 'pointer';
          }
        });
      } catch {
        /* 어포던스는 부가 기능 — 실패해도 무시 */
      }
    };

    const handleClick = (view: dia.CellView) => {
      const model = view.model;
      if (!model.isElement()) return;
      const bell = matchBell(model as dia.Element);
      if (!bell) return;
      useSimInteractionStore.getState().requestBellTrigger(bell.id, 'alert');
    };

    paper.on('cell:pointerclick', handleClick);
    applyCursorAffordance();

    return () => {
      paper.off('cell:pointerclick', handleClick);
    };
  }, [graph, paper, bells]);

  return null;
}
